import { Star, Tick } from '@components/icons';
import { Locale } from '@core/constants/locales.constants';
import { Profile } from '@graphql/__generated__/graphql';
import { Translations } from '@lib/dictionary-keys/__generated__';
import { CleanContentful } from '@stadionhq-v3/clean-contentful';
import { ContentfulImage } from '@stadionhq-v3/contentful-ui';
import { PlayerStatus, ProfileListingsProps } from '@stadionhq-v3/football-ui';
import templateConfig from '@templateConfig';
import { generateFullPathname } from '@utils/paths/generateFullPathname.utils';
import Image from 'next/image';
import FallbackImage from '../../../../../public/images/Leeds-United-Teams.png';

type PlayerStatsByTeamEntry = { teamId?: string; caps?: number | string };
type PlayerStatsBlob = { byTeam?: PlayerStatsByTeamEntry[] };

// Flat (cleanContentful-shaped) image — same shape as profile.profileImage.
type FlatImage = NonNullable<CleanContentful<Profile>['profileImage']>;

const getCapsForTeam = (
  playerStats: unknown,
  teamId: string | undefined
): string | undefined => {
  if (!teamId) return undefined;
  const blob = playerStats as PlayerStatsBlob | null | undefined;
  const match = blob?.byTeam?.find((entry) => entry?.teamId === teamId);
  if (match?.caps == null) return undefined;
  return String(match.caps);
};

const profileListingsTransform = (
  items: (CleanContentful<Profile> | undefined)[] | undefined,
  dictionary: {
    caption: Translations['profile']['label']['captain'];
    isHomegrown: Translations['profile']['label']['homeGrown'];
  },
  team: string,
  locale: Locale,
  options: {
    showCapsInsteadOfPosition?: boolean;
    teamId?: string;
    /** Team's playerProfileFallbackImage from Contentful; used when a profile has no profileImage of its own. */
    teamFallbackImage?: FlatImage | null;
    /** Youth teams link to /teams/youth/... — picks PROFILE_YOUTH so cards
     * resolve directly instead of hitting the canonical redirect. */
    isYouthTeam?: boolean;
  } = {}
): ProfileListingsProps => {
  const sortedItems = (items ?? []).slice().sort((a, b) => {
    const aHasSquad = !!a?.squadNumber;
    const bHasSquad = !!b?.squadNumber;

    if (aHasSquad && bHasSquad) {
      return (a!.squadNumber as number) - (b!.squadNumber as number);
    }
    if (aHasSquad) return -1;
    if (bHasSquad) return 1;

    // Neither has squad number, sort by knownAs, then lastName
    const aName = a?.knownAs || a?.lastName || '';
    const bName = b?.knownAs || b?.lastName || '';
    return aName.localeCompare(bName, locale, { sensitivity: 'base' });
  });

  return {
    labels: {},
    profiles: sortedItems.map((profile) => ({
      image: profile?.profileImage ? (
        <ContentfulImage
          key={profile.profileImage.id}
          {...profile.profileImage}
          focalPoint={profile.profileImage.focalPoint || { x: 1, y: 1 }}
          fit="fill"
          focus="top"
          breakpoints={['s']}
          customImageSizes={{
            360: 600,
          }}
        />
      ) : options.teamFallbackImage?.url ? (
        <ContentfulImage
          key={options.teamFallbackImage.id}
          {...options.teamFallbackImage}
          focalPoint={options.teamFallbackImage.focalPoint || { x: 1, y: 1 }}
          fit="fill"
          focus="top"
          breakpoints={['s']}
          customImageSizes={{
            360: 600,
          }}
        />
      ) : (
        <Image
          src={FallbackImage}
          alt="fallback image"
          layout="fill"
          objectFit="cover"
        />
      ),
      // Flags intentionally omitted — all players are Scotland-affiliated so
      // the country flag adds nothing to the listing card.
      isCaptain: profile?.isCaptain ? (
        <PlayerStatus
          icon={<Star />}
          label={dictionary.caption}
          iconPosition="left"
          variant="no-label"
        />
      ) : (
        false
      ),
      isHomegrown: profile?.isHomegrown ? (
        <PlayerStatus
          icon={<Tick />}
          label={dictionary.isHomegrown}
          iconPosition="left"
          variant="no-label"
        />
      ) : (
        false
      ),
      nationality: profile?.nationality?.shortName || '',
      squadNumber: profile?.squadNumber?.toString(),
      firstName: profile?.firstName || '',
      surname: profile?.lastName || '',
      knownAs: profile?.knownAs || '',
      url: generateFullPathname({
        locale,
        route: options.isYouthTeam
          ? templateConfig.navigation.routes.PROFILE_YOUTH
          : templateConfig.navigation.routes.PROFILE,
        params: {
          team,
          profile: profile?.slug || '',
        },
      }),
      position: profile?.position || '',
      ...(options.showCapsInsteadOfPosition
        ? (() => {
            const caps = getCapsForTeam(profile?.playerStats, options.teamId);
            return caps ? { caps } : {};
          })()
        : {}),
    })),
  };
};

export default profileListingsTransform;
